import React, {
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
  ReactNode,
  createContext,
  useContext,
} from "react";
import {
  Stack,
  Box,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import {
  DataGrid,
  DEFAULT_GRID_AUTOSIZE_OPTIONS,
  GridRowClassNameParams,
  GridRowId,
  GridRowIdGetter,
  useGridApiRef,
  type GridColDef,
  type GridInitialState,
  type GridAutosizeOptions,
  type GridPaginationModel,
  type GridApi,
} from "@mui/x-data-grid";
import type { MutableRefObject } from "react";
import { CustomNoRowsOverlay } from "./SbuxGenericOverlay";
import SbuxLoader from "../SbuxLoader";
import SbuxTranslate from "../SbuxTranslate";
import useTextTranslate from "../../features/content/hooks/useTextTranslate";

import { DATAGRID_DEFAULT_PAGE_SIZE } from "../../constants";
import { calcPageSizeOptions, mapRowIdentifier } from "../../utils/tableUtil";
import { useDebounce } from "../../utils/commonUtil";
import styles from "./styles";
import useCss from "../../hooks/useCss";
import { normalizeProps, getRowClasses, calcRowIdFn } from "./util";

// Context for sharing GridApi with toolbar components
const GridApiContext = createContext<MutableRefObject<GridApi> | null>(null);
export const useGridApiContext = () => useContext(GridApiContext);

interface GridToolbarProps {
  className?: string;
  searchPlaceholder?: ReactNode;
  children?: ReactNode;
  showQuickFilter?: boolean;
  apiRef?: MutableRefObject<GridApi>;
  leftContent?: ReactNode;
}

interface GridContainerProps {
  className?: string;
  autosizeColumnsCallback: (() => void) | null;
  children?: ReactNode;
}

interface SlotClasses {
  toolbar?: { container?: string };
  datagrid?: { container?: string };
}

interface SlotProps {
  pagination?: Record<string, unknown>;
}

interface GenericGridProps {
  apiRef?: MutableRefObject<GridApi>;
  rows?: Record<string, unknown>[];
  columns?: GridColDef[];
  isLoading?: boolean;
  initialState?: GridInitialState;
  autosizeOptions?: Partial<GridAutosizeOptions>;
  columnBufferPx?: number;
  sortingMode?: "client" | "server";
  slotClasses?: SlotClasses;
  slotProps?: SlotProps;
  slots?: { toolbar?: React.ComponentType };
  onPaginationModelChange?: (
    model: { page: number; pageSize: number },
    details: unknown,
  ) => void;
  labelRowsPerPage?: ReactNode;
  labelOf?: ReactNode;
  clickableRow?: boolean;
  showQuickFilter?: boolean;
  [key: string]: unknown;
}

const defaultPaginationModel = {
  pageSize: DATAGRID_DEFAULT_PAGE_SIZE,
  page: 0,
};

const defaultAutosizeOptions = {
  includeOutliers: true,
  includeHeaders: true,
  expand: true,
  outliersFactor: DEFAULT_GRID_AUTOSIZE_OPTIONS.outliersFactor,
};

// Column virtualization configuration
const defaultColumnBufferPx = 150; // Default buffer for columns coming under 150px region

const debounceMs = 250;

/**
 * SbuxGridToolbar toolbar component
 * Renders directly without portal for immediate visibility
 * Uses custom TextField for quick filter (works outside DataGrid context)
 * @param {Object} props  component props
 */
export const SbuxGridToolbar = React.memo(
  ({
    className,
    searchPlaceholder,
    children,
    showQuickFilter,
    apiRef: propApiRef,
    leftContent,
  }: GridToolbarProps) => {
    const theme = useTheme();
    const classes = useCss(styles);
    const contextApiRef = useGridApiContext();
    // Use prop apiRef if provided, otherwise use context
    const apiRef = propApiRef ?? contextApiRef;
    const classList = [classes.gridToolbar];
    className && classList.push(className);
    const resolvedClassName = classList.join(" ");
    // Only translation logic for search placeholder
    const translatedSearch = useTextTranslate("Search...");
    const placeholderValue =
      typeof searchPlaceholder === "string"
        ? searchPlaceholder
        : translatedSearch;

    // Local state to maintain search value across re-renders
    const [searchValue, setSearchValue] = useState("");

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);
      if (apiRef?.current) {
        apiRef.current.setQuickFilterValues(value ? [value] : []);
      }
    };

    const handleClearSearch = () => {
      setSearchValue("");
      if (apiRef?.current) {
        apiRef.current.setQuickFilterValues([]);
      }
    };

    return (
      <Stack
        direction="row"
        className={resolvedClassName}
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: leftContent ? "space-between" : "flex-end",
          flexWrap: "nowrap",
        }}
      >
        {/* Left section */}
        {leftContent && (
          <Box sx={{ flexShrink: 0, flexGrow: 0, marginRight: 1 }}>
            {leftContent}
          </Box>
        )}
        {/* Right section */}
        <Stack
          direction="row"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 1,
            flexShrink: 0,
            flexGrow: 0,
            justifyContent: "flex-end",
          }}
        >
          {children}
          {showQuickFilter !== false && (
            <Box sx={{ width: 150, flexShrink: 0, flexGrow: 0 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder={placeholderValue}
                value={searchValue}
                onChange={handleSearchChange}
                sx={{
                  width: 150,
                  "& .MuiOutlinedInput-root": {
                    width: 150,
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          edge="end"
                          aria-label="Clear"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  },
);

/**
 * GridContainer component that will cotain the instance of DataGrid and will call
 * a autosizeColumnsCallback function when the ResizeObserver detects a change
 * @param {Object} props  component props
 */
const GridContainer = ({
  className,
  autosizeColumnsCallback,
  children,
}: GridContainerProps) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const callbackFn = useDebounce((entries: ResizeObserverEntry[]) => {
    if (entries && typeof autosizeColumnsCallback === "function") {
      autosizeColumnsCallback();
    }
  }, 350);

  useLayoutEffect(() => {
    const targetEl = boxRef.current;

    if (!targetEl) return;

    const resizeObserver = new ResizeObserver(callbackFn);
    resizeObserver.observe(targetEl);

    return () => {
      resizeObserver.unobserve(targetEl);
    };
  }, [callbackFn]);

  return (
    <Box
      ref={boxRef}
      className={className}
      sx={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}
    >
      {children}
    </Box>
  );
};

export const NoRowsOverlay = () => (
  <CustomNoRowsOverlay>
    <SbuxTranslate>{"No data found"}</SbuxTranslate>
  </CustomNoRowsOverlay>
);

/**
 * SbuxGenericGrid component
 * @param {Object} props  component props
 */
export const SbuxGenericGrid = (props: GenericGridProps) => {
  const theme = useTheme();
  const classes = useCss(styles);
  const internalApiRef = useGridApiRef();
  const apiRef = props.apiRef ?? internalApiRef;

  const rowClassesMap = getRowClasses(props);

  // Important
  const normalProps = normalizeProps(props);

  const initPaginationModel: GridPaginationModel =
    (props.initialState?.pagination?.paginationModel as
      | GridPaginationModel
      | undefined) ?? defaultPaginationModel;
  const autosizeOptions = {
    ...defaultAutosizeOptions,
    ...(props.autosizeOptions ?? {}),
  };
  const columnBufferPx = props.columnBufferPx ?? defaultColumnBufferPx;
  const shouldTriggerAutoresize =
    autosizeOptions.includeHeaders || autosizeOptions.expand;

  /* Pagination specs seems to need to be kept in a component.state */
  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(initPaginationModel);
  const [pageSizeOptions] = useState(
    calcPageSizeOptions(paginationModel.pageSize),
  );

  const dataset = mapRowIdentifier(props.rows ?? []);
  const getRowIdFn = calcRowIdFn(dataset[0]);

  /**
   * Invokes the DataGrid Api to resize the columns with the default configuration
   * Feature can be disabled via config
   */
  const autosizeColumns = shouldTriggerAutoresize
    ? () => {
        if (
          apiRef &&
          apiRef.current &&
          typeof apiRef.current.autosizeColumns === "function"
        ) {
          apiRef.current.autosizeColumns(autosizeOptions);
        }
      }
    : null;

  /**
   * Handles onPaginationModelChange event
   * @param  {Object} model                 new pagination model
   * @param  {Object} details               pagination details
   */
  const handleOnPaginationModelChange = (
    model: { page: number; pageSize: number },
    details: unknown,
  ) => {
    setPaginationModel(model);

    if (typeof props.onPaginationModelChange === "function") {
      props.onPaginationModelChange(model, details);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const prevRowsLengthRef = useRef(props.rows?.length ?? 0);

  // Reset pagination when switching to a different dataset
  useEffect(() => {
    if (props.rows) {
      const currentLength = props.rows.length;
      const prevLength = prevRowsLengthRef.current;

      // Detect table switch (significant change in row count)
      const TABLE_SWITCH_THRESHOLD = 100;
      const isTableSwitch =
        Math.abs(currentLength - prevLength) > TABLE_SWITCH_THRESHOLD ||
        (prevLength > 0 && currentLength === 0) ||
        (prevLength === 0 && currentLength > 0);

      if (isTableSwitch) {
        setIsLoading(true);
        setPaginationModel((prev) => ({
          ...prev,
          page: 0,
        }));

        // Brief loading state to allow UI transition
        requestAnimationFrame(() => {
          setIsLoading(false);
        });
      }

      prevRowsLengthRef.current = currentLength;
    }
  }, [props.rows]);

  // Add to the component or create a separate utility function
  const getGridTranslations = () => {
    return {
      // Pagination texts
      MuiTablePagination: {
        labelRowsPerPage: props.labelRowsPerPage || (
          <SbuxTranslate>{"Rows per page"}</SbuxTranslate>
        ),
        labelDisplayedRows: ({
          from,
          to,
          count,
        }: {
          from: number;
          to: number;
          count: number;
        }) => {
          const ofText = props.labelOf || <SbuxTranslate>{"of"}</SbuxTranslate>;
          return (
            <span>
              {from}-{to} {ofText} {count !== -1 ? count : `more than ${to}`}
            </span>
          );
        },
      },
    };
  };

  const getRowClassNameFn = (config: GridRowClassNameParams): string => {
    const idx = config.indexRelativeToCurrentPage % 2;
    return rowClassesMap.get(idx) ?? "";
  };

  const getRowIdFnTyped: GridRowIdGetter<Record<string, unknown>> = (row) => {
    const id = getRowIdFn(row);
    return id as GridRowId;
  };

  // Get the toolbar component to render directly above the grid
  const ToolbarComponent = props.slots?.toolbar;

  return (
    <GridApiContext.Provider value={apiRef ?? null}>
      <Stack
        spacing={0}
        direction="column"
        sx={{ width: "100%", height: "100%" }}
      >
        {/* Render toolbar directly - not as a DataGrid slot */}
        <Box
          id="grid-toolbar"
          className={props.slotClasses?.toolbar?.container}
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: "48px",
            width: "100%",
            flexWrap: "nowrap",
            gap: 1,
            padding: "2px 0",
            flex: "0 0 auto",
          }}
        >
          {ToolbarComponent && <ToolbarComponent />}
        </Box>

        <GridContainer
          className={props.slotClasses?.datagrid?.container}
          autosizeColumnsCallback={autosizeColumns}
        >
          {!props.isLoading && !isLoading && props.rows ? (
            <DataGrid
              className={classes.dataGridRoot}
              sx={{ height: "100%", width: "100%" }}
              /* Default configuration */
              slots={{
                noRowsOverlay: NoRowsOverlay,
              }}
              slotProps={{
                pagination: {
                  showFirstButton: true,
                  showLastButton: true,
                  ...(props.slotProps?.pagination ?? {}),
                },
              }}
              sortingMode={props?.sortingMode ?? "client"}
              columnBufferPx={columnBufferPx}
              initialState={{
                ...props.initialState,
                pagination: {
                  ...props.initialState?.pagination,
                  paginationModel: paginationModel,
                },
                filter: {
                  filterModel: {
                    items: [],
                    quickFilterExcludeHiddenColumns: false,
                  },
                },
              }}
              pageSizeOptions={pageSizeOptions}
              paginationModel={paginationModel}
              onPaginationModelChange={handleOnPaginationModelChange}
              isRowSelectable={() => false}
              disableColumnMenu
              rowHeight={32}
              autosizeOnMount
              autosizeOptions={autosizeOptions}
              localeText={getGridTranslations()}
              /* props to override default configuration if contained it */
              {...normalProps}
              /* data related configuration */
              columns={props.columns ?? []}
              rows={dataset} // row identifier added
              getRowId={getRowIdFnTyped}
              getRowClassName={getRowClassNameFn}
              /* DataGrid Api */
              apiRef={apiRef}
              loading={false}
            />
          ) : (
            <SbuxLoader />
          )}
        </GridContainer>
      </Stack>
    </GridApiContext.Provider>
  );
};
